import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';
import { Reveal, Eyebrow } from '../components/Reveal';
import api from '../lib/api';

const FIELDS = [
  ['background', 'Project Background'],
  ['challenge', 'The Challenge'],
  ['objectives', 'Objectives'],
  ['scope_of_work', 'Scope of Work'],
  ['methodology', 'Research Methodology'],
  ['deliverables', 'Deliverables'],
  ['outcomes', 'Outcomes & Recommendations'],
];

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/case-studies/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-brand-offwhite px-5 pt-24" data-testid="case-study-not-found">
        <h1 className="font-heading text-3xl font-bold text-brand-black">Case study not found</h1>
        <p className="text-sm text-brand-body">This case study may be unpublished or confidential.</p>
        <Link to="/case-studies" className="flex items-center gap-2 font-heading text-sm font-bold text-brand-deepgreen"><ArrowLeft size={16} /> Back to Case Studies</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-brand-offwhite pt-24"><div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-green border-t-transparent" /></div>;
  }

  const cs = data.case_study;

  return (
    <>
      <SEO title={`${cs.title} | INSIGHTS STRATEGY Case Studies`} description={(cs.background || cs.title).slice(0, 155)} path={`/case-studies/${slug}`} />
      <section className="bg-brand-deep" data-testid="case-study-hero">
        <div className="mx-auto max-w-5xl px-5 pb-16 pt-40 lg:px-8">
          <Link to="/case-studies" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-brand-green" data-testid="case-study-back">
            <ArrowLeft size={14} /> All Case Studies
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-brand-green">{cs.category}</p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">{cs.title}</h1>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs text-white/55">
            {cs.sector && <span><span className="text-white/35">Sector:</span> {cs.sector}</span>}
            {cs.geography && <span><span className="text-white/35">Geography:</span> {cs.geography}</span>}
            {cs.client_type && <span><span className="text-white/35">Client:</span> {cs.client_type}</span>}
          </div>
        </div>
      </section>

      {cs.cover_image && (
        <div className="mx-auto -mt-0 max-w-5xl px-5 lg:px-8">
          <img src={cs.cover_image} alt={cs.title} className="aspect-[21/9] w-full rounded-2xl object-cover" data-testid="case-study-cover" />
        </div>
      )}

      <section className="bg-brand-offwhite">
        <div className="mx-auto max-w-5xl space-y-12 px-5 py-16 lg:px-8 lg:py-24">
          {FIELDS.map(([key, label], i) =>
            cs[key] ? (
              <Reveal key={key} delay={0.03 * i}>
                <div className="grid gap-4 lg:grid-cols-12" data-testid={`case-field-${key}`}>
                  <h2 className="font-heading text-sm font-extrabold uppercase tracking-[0.18em] text-brand-deepgreen lg:col-span-3">{label}</h2>
                  <div className="whitespace-pre-wrap text-base leading-relaxed text-brand-body lg:col-span-9">{cs[key]}</div>
                </div>
              </Reveal>
            ) : null
          )}
        </div>
      </section>

      {data.related?.length > 0 && (
        <section className="bg-white" data-testid="case-study-related">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
            <Eyebrow>Related Case Studies</Eyebrow>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.related.map((r) => (
                <Link key={r.case_id} to={`/case-studies/${r.slug}`} className="group rounded-2xl border border-brand-grey bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,13,11,0.08)]" data-testid={`related-case-${r.slug}`}>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-deepgreen">{r.category}</span>
                  <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-black">{r.title}</h3>
                  <span className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-deepgreen">Read <ArrowUpRight size={14} /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
